/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable no-plusplus */
import { QUESTIONNAIRE_ELEMENT_TYPE } from '../../questionnaireTemplate.enums';
import { QuestionnaireDragSource } from '../../questionnaireTemplate.types';
import { formElementSchema } from '../schemas/formElement.schema';

/**
 * Determines if dropping an element into a zone should be blocked.
 * Checks for nesting restrictions and maximum children limits.
 *
 * @param closestZone - The DOM element representing the target drop zone
 * @param closestZoneData - Metadata about the drop zone (parent ID, branch, index)
 * @param dragSource - Information about the element being dragged
 * @returns Object containing block status and reason flags
 */
export const handleElementBlock = ({
  closestZone,
  closestZoneData,
  dragSource,
}: {
  closestZone: HTMLElement | null;
  closestZoneData: { parentId: string | null; branch: string | null; index: number } | null;
  dragSource: QuestionnaireDragSource;
}) => {
  // Early return if no valid drop zone data
  if (!closestZoneData?.parentId || !closestZone) {
    return { isBlocked: false, isMaxDepthLimit: false };
  }

  // Cache the dragging element type for reuse
  const draggingType = dragSource.templateType;

  // Check parent element restrictions
  const parentDomElement = document.querySelector(`[data-element-id="${closestZoneData.parentId}"]`);
  const parentType = parentDomElement?.getAttribute('data-element-type') as QUESTIONNAIRE_ELEMENT_TYPE;

  // Rule 1: Prevent nesting logic elements inside other logic elements
  const isLogicNestingBlocked = checkLogicNesting(draggingType, parentType);

  // Rule 2: Enforce maximum children count for the parent's branch
  const isMaxChildrenExceeded = checkMaxChildren(closestZone, parentType);

  return {
    isBlocked: isLogicNestingBlocked || isMaxChildrenExceeded,
    isMaxDepthLimit: isMaxChildrenExceeded,
  };
};

/**
 * Checks if nesting a logic element inside another logic element is blocked.
 * Logic elements (YESNO types) cannot be nested within each other.
 *
 * @param draggingType - Type of element being dragged
 * @param parentType - Type of the parent element
 * @returns true if nesting should be blocked
 */
const checkLogicNesting = (draggingType: QUESTIONNAIRE_ELEMENT_TYPE, parentType: QUESTIONNAIRE_ELEMENT_TYPE): boolean => {
  // Define logic element types (can be moved to constants file if used elsewhere)
  const LOGIC_TYPES = new Set([QUESTIONNAIRE_ELEMENT_TYPE.YESNO, QUESTIONNAIRE_ELEMENT_TYPE.YESNO_WITH_IMAGE]);

  return LOGIC_TYPES.has(draggingType) && LOGIC_TYPES.has(parentType);
};

/**
 * Checks if the parent element has reached its maximum allowed children count.
 *
 * @param closestZone - The target drop zone element
 * @param parentType - Type of the parent element
 * @returns true if maximum children limit is reached
 */
const checkMaxChildren = (closestZone: HTMLElement, parentType: QUESTIONNAIRE_ELEMENT_TYPE): boolean => {
  // Get maximum allowed children from schema (defaults to Infinity if not specified)
  const maxAllowed = formElementSchema[parentType]?.maxBranchChildrenCount ?? Infinity;

  // If no limit is set, skip the expensive DOM query
  if (maxAllowed === Infinity) {
    return false;
  }

  // Count current children (excluding dropzones and the closest zone itself)
  const currentCount = countSiblings(closestZone);

  return currentCount >= maxAllowed;
};

/**
 * Counts non-dropzone siblings of the given element.
 *
 * @param element - The reference element
 * @returns Count of sibling elements (excluding dropzones and the element itself)
 */
const countSiblings = (element: HTMLElement): number => {
  const parent = element.parentElement;
  if (!parent) return 0;

  let count = 0;
  // Use childNodes for better performance than Array.from + filter
  for (let i = 0; i < parent.children.length; i++) {
    const child = parent.children[i];
    // Skip the element itself and any dropzones
    if (child !== element && !child.hasAttribute('data-dropzone')) {
      count++;
    }
  }

  return count;
};
